import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import {
  ContainerCaseFromShipmentDeliveryRequest,
  ContainerCaseFromShipmentDeliveryResult,
  ContainerReferenceSuggestion,
  ContainerReferenceType,
  ContainerTrackingService
} from '../../container-tracking/container-tracking.service';
import { ContainerJourneyTemplateView } from '../../container-tracking/models/container-journey-plan.model';
import { SessionStorageService } from '../../services/session-storage.service';

interface JourneyProfileOption {
  label: string;
  value: number;
  description: string;
  stepCount: number;
}

type CommercialReferenceField = 'bookingNumber' | 'billOfLadingNumber' | 'houseBillNumber' | 'customsDeclarationNumber';

@Component({
  selector: 'app-delivery-container-case-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AutoCompleteModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule
  ],
  templateUrl: './delivery-container-case-dialog.component.html',
  styleUrls: ['./delivery-container-case-dialog.component.css']
})
export class DeliveryContainerCaseDialogComponent {
  @Input() visible = false;
  @Input() deliveryId?: number;
  @Input() deliveryNumber?: string;
  @Input() shipmentNumber?: string;
  @Input() releaseNumber?: string;
  @Input() routeLabel?: string;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() created = new EventEmitter<ContainerCaseFromShipmentDeliveryResult>();

  creating = false;
  form: ContainerCaseFromShipmentDeliveryRequest = this.emptyForm();
  journeyProfileOptions: JourneyProfileOption[] = [];
  journeyProfilesLoading = false;
  referenceSuggestions: Record<ContainerReferenceType, ContainerReferenceSuggestion[]> = {
    BOOKING: [],
    BILL_OF_LADING: [],
    HOUSE_BILL: [],
    CUSTOMS_DECLARATION: []
  };

  readonly equipmentTypeOptions = [
    { label: '20GP', value: '20GP' },
    { label: '40GP', value: '40GP' },
    { label: '40HC', value: '40HC' },
    { label: '45HC', value: '45HC' },
    { label: '20RF', value: '20RF' },
    { label: '40RF', value: '40RF' },
    { label: 'Tank', value: 'TANK' },
    { label: 'Flat Rack', value: 'FLAT_RACK' },
    { label: 'Open Top', value: 'OPEN_TOP' }
  ];
  readonly transportModeOptions = [
    { label: 'Truck movement', value: 'TRUCK' },
    { label: 'Terminal transfer', value: 'TERMINAL_TRANSFER' },
    { label: 'Yard transfer', value: 'YARD_TRANSFER' },
    { label: 'Customer delivery', value: 'CUSTOMER_DELIVERY' },
    { label: 'Empty return', value: 'EMPTY_RETURN' }
  ];

  constructor(
    private containerTrackingService: ContainerTrackingService,
    private sessionStorageService: SessionStorageService,
    private messageService: MessageService
  ) {}

  onShow(): void {
    this.reset();
    this.loadJourneyProfiles();
  }

  close(): void {
    if (!this.creating) {
      this.visibleChange.emit(false);
    }
  }

  reset(): void {
    this.form = this.emptyForm();
    this.clearReferenceSuggestions();
  }

  selectedJourneyProfile(): JourneyProfileOption | undefined {
    return this.journeyProfileOptions.find(option => option.value === this.form.journeyTemplateId);
  }

  create(): void {
    const containerNumber = this.form.containerNumber?.trim();
    if (!this.deliveryId || !containerNumber) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Container required',
        detail: 'Enter the container number before creating the lifecycle case.'
      });
      return;
    }
    if (!this.hasCommercialReference()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Commercial reference required',
        detail: 'Enter at least a booking number or bill of lading before creating the lifecycle case.'
      });
      return;
    }
    if (!this.form.journeyTemplateId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Equipment journey plan required',
        detail: 'Select the equipment journey plan that will seed the container journey.'
      });
      return;
    }

    this.creating = true;
    this.containerTrackingService.createCaseFromShipmentDelivery(this.deliveryId, {
      ...this.form,
      containerNumber,
      createdBy: this.sessionStorageService.getCustomer()?.id ?? null
    }).pipe(finalize(() => this.creating = false))
      .subscribe({
        next: result => {
          this.visibleChange.emit(false);
          this.created.emit(result);
        },
        error: error => {
          this.messageService.add({
            severity: 'error',
            summary: 'Container case not created',
            detail: error?.error?.message || 'Could not create the container case from this delivery.'
          });
        }
      });
  }

  searchReferences(referenceType: ContainerReferenceType, query: string): void {
    const owningCompanyId = this.sessionStorageService.getCompany()?.id;
    if (!owningCompanyId) {
      this.referenceSuggestions[referenceType] = [];
      return;
    }
    this.containerTrackingService.findReferenceSuggestions(owningCompanyId, referenceType, query || '')
      .subscribe({
        next: suggestions => this.referenceSuggestions[referenceType] = suggestions || [],
        error: () => this.referenceSuggestions[referenceType] = []
      });
  }

  applyReferenceSuggestion(suggestion: ContainerReferenceSuggestion): void {
    if (!suggestion?.value) {
      return;
    }
    const patch: Partial<ContainerCaseFromShipmentDeliveryRequest> = {
      [this.referenceField(suggestion.referenceType)]: suggestion.value
    };
    this.patchReferenceWhenEmpty(patch, 'bookingNumber', suggestion.bookingNumber);
    this.patchReferenceWhenEmpty(patch, 'billOfLadingNumber', suggestion.billOfLadingNumber);
    this.patchReferenceWhenEmpty(patch, 'houseBillNumber', suggestion.houseBillNumber);
    this.patchReferenceWhenEmpty(patch, 'customsDeclarationNumber', suggestion.customsDeclarationNumber);
    this.form = { ...this.form, ...patch };
  }

  private loadJourneyProfiles(): void {
    const owningCompanyId = this.sessionStorageService.getCompany()?.id;
    if (!owningCompanyId) {
      this.journeyProfileOptions = [];
      return;
    }
    this.journeyProfilesLoading = true;
    this.containerTrackingService.findJourneyTemplates(owningCompanyId)
      .pipe(finalize(() => this.journeyProfilesLoading = false))
      .subscribe({
        next: profiles => {
          this.journeyProfileOptions = (profiles || [])
            .filter(profile => profile.template?.id != null)
            .map(profile => this.toJourneyProfileOption(profile));
        },
        error: () => {
          this.journeyProfileOptions = [];
          this.messageService.add({
            severity: 'warn',
            summary: 'Journey profiles unavailable',
            detail: 'Reload the profiles before creating the container case.'
          });
        }
      });
  }

  private toJourneyProfileOption(profile: ContainerJourneyTemplateView): JourneyProfileOption {
    const template = profile.template!;
    return {
      label: [template.templateName, template.templateCode].filter(Boolean).join(' | '),
      value: template.id!,
      description: template.journeyType || template.description || 'Reusable container journey profile',
      stepCount: profile.items?.length || 0
    };
  }

  private emptyForm(): ContainerCaseFromShipmentDeliveryRequest {
    return {
      containerNumber: '',
      isoEquipmentType: null,
      sealNumber: '',
      bookingNumber: '',
      billOfLadingNumber: '',
      houseBillNumber: '',
      customsDeclarationNumber: '',
      transportMode: 'TRUCK',
      journeyTemplateId: null
    };
  }

  private referenceField(referenceType: ContainerReferenceType): CommercialReferenceField {
    switch (referenceType) {
      case 'BOOKING': return 'bookingNumber';
      case 'BILL_OF_LADING': return 'billOfLadingNumber';
      case 'HOUSE_BILL': return 'houseBillNumber';
      case 'CUSTOMS_DECLARATION': return 'customsDeclarationNumber';
    }
  }

  private patchReferenceWhenEmpty(
    patch: Partial<ContainerCaseFromShipmentDeliveryRequest>,
    field: CommercialReferenceField,
    value?: string | null
  ): void {
    if (value && !this.form[field]) {
      (patch as Record<CommercialReferenceField, string | null | undefined>)[field] = value;
    }
  }

  private clearReferenceSuggestions(): void {
    (Object.keys(this.referenceSuggestions) as ContainerReferenceType[])
      .forEach(type => this.referenceSuggestions[type] = []);
  }

  hasCommercialReference(): boolean {
    return Boolean(this.form.bookingNumber?.trim() || this.form.billOfLadingNumber?.trim());
  }
}
